// File: src/data/deeperNotFaster.js
// Source: a research sweep run on 2026-08-31. Eight parallel passes, roughly 400 web
// calls, checking whether each "agentic AI" sub-industry has a real evaluator, a real
// buyer, and independent evidence.

export const VERDICTS = ["SERIOUS", "EMERGING", "FANTASY"];

export const verdictMeaning = {
  SERIOUS: "A real evaluator, real paying customers, and evidence from somewhere other than the company's own blog.",
  EMERGING: "An evaluator exists, but the business or the proof does not yet.",
  FANTASY: "No hard evaluator, no buyer, or a story that runs well ahead of the evidence.",
};

export const modeMeaning = {
  deeper: "The agents open up a search space: many attempts, non-obvious options, things that were not possible before.",
  faster: "The agents compress work humans already did.",
  mixed: "Part of the field goes deeper, part of it is just faster.",
};

export const pathMeaning = {
  A: "Applied agentic. Many customers, doable today.",
  B: "Deep tech. Few niche customers, long payoff.",
};

export const clusters = [
  {
    id: "science",
    title: "Autonomous science",
    digest:
      "The evaluator test sorts this one cleanly. Firms that own their lab (Lila, Periodic, Chemify, Dunia in Berlin, alqem in Munich) and lab-in-the-loop protein SaaS (Cradle) pass. Literature and hypothesis agents (Edison, Sakana) only pass through somebody else's lab. Revenue evidence is thin and mostly company-sourced, and the first generation of \"AI plus lab\" companies mostly stalled.",
    rows: [
      {
        n: 1,
        name: "Self-driving labs for materials and chemistry",
        verdict: "EMERGING",
        verdictNote: "the technology is real, the business is unproven",
        mode: "deeper",
        path: "B",
        evaluator: "Its own robotic synthesis and characterisation lab.",
        where:
          "Berlin (Dunia), Munich (alqem, 8M euro pre-seed in Jul 2026), Glasgow (Chemify), Basel and Lausanne (Atinary). All research roles, all on site.",
        evidence:
          "Lila raised $550M by Oct 2025 with 127 open roles, all R&D (HIGH). Periodic Labs $300M seed (HIGH). The first-generation cohort stalled: Kebotix down to 12 staff, Citrine flat, Ginkgo revenue down 37%, Orbital pivoted (MEDIUM).",
      },
      {
        n: 2,
        name: "AI-scientist agents: hypothesis, literature, planning",
        verdict: "EMERGING",
        verdictNote: "emerging as tooling, fantasy as an \"autonomous scientist\"",
        mode: "deeper",
        modeNote: "deeper only via the customer's lab",
        path: "A",
        evaluator: "The customer's wet lab, which is somebody else's building.",
        where: "Edison Scientific in San Francisco. No European equivalent found.",
        evidence:
          "Sakana's AI-Scientist: 42% of experiments failed, numbers were hallucinated, and acceptance was workshop-track only (HIGH, arXiv 2502.14297). DeepMind's own Jul 2026 essay concedes a \"validation bottleneck\" (HIGH). The Qiushi optical-bench paper is real but academic and unreplicated (HIGH, arXiv 2604.27092).",
      },
      {
        n: 3,
        name: "Protein and enzyme design SaaS, lab-in-the-loop",
        verdict: "SERIOUS",
        mode: "deeper",
        path: "A",
        evaluator: "Customer assay data comes back every design round.",
        where: "Cradle (Amsterdam, Zurich, Boston, 124 staff). Latent Labs in London.",
        evidence:
          "Cradle: 6 of the top 25 pharma companies, 50+ projects, $73M Series B (MEDIUM). Profluent counts Revvity, Corteva and IDT as customers (MEDIUM).",
      },
      {
        n: 4,
        name: "AI drug-discovery pipelines",
        verdict: "SERIOUS",
        verdictNote: "serious money, sceptical outcomes",
        mode: "deeper",
        path: "B",
        evaluator: "The clinic, years later.",
        where: "Isomorphic in London is a drug company, not a vendor.",
        evidence:
          "No fully AI-designed drug has been approved. BenevolentAI fell 99% and delisted. Exscientia failed and was absorbed by Recursion, which then cut 20% of staff (MEDIUM). Isomorphic raised $2.1B on 12 May 2026 (HIGH).",
      },
    ],
  },
  {
    id: "math",
    title: "Math, formal verification, chips, RL environments",
    digest:
      "Hard Lean and oracle loops are real, and so are the results (Tao times 67, Gauss, Axiom proofs in five journals, Glasswing-scale code search). But roughly $700M of math-AI venture money has no named customers. The only \"many customers\" shape is AlphaEvolve on Google Cloud. Formal verification is a Paris-heavy legacy industry bolting agents onto a prover. Chip-design agents genuinely sell, but ship as copilots.",
    rows: [
      {
        n: 5,
        name: "Math and algorithm discovery",
        verdict: "SERIOUS",
        verdictNote: "serious as science, emerging as a business",
        mode: "deeper",
        path: "B",
        pathNote: "A only via Google Cloud",
        evaluator: "The Lean 4 kernel, plus per-problem verification code.",
        where: "Google Cloud (AlphaEvolve went GA in Jul 2026, customers BASF, JetBrains, Kinaxis) and Mistral's Leanstral team in Paris.",
        evidence:
          "About $700M into Harmonic ($120M at $1.45B, HIGH), Axiom ($200M at $1.6B, HIGH, reportedly zero revenue) and Math Inc, with no named paying customers. The Tao and AlphaEvolve 67-problem paper is confirmed (arXiv 2511.02864). DeepMind's own Aletheia authors call the four solved Erdos problems \"elementary\".",
      },
      {
        n: 6,
        name: "Formal verification for industry, the legacy vendors",
        verdict: "SERIOUS",
        mode: "faster",
        modeNote: "faster and safer",
        path: "A",
        evaluator: "The prover.",
        where: "A Paris cluster: AdaCore, TrustInSoft, Formal Land. Certora serves crypto.",
        evidence: "TrustInSoft shipped AI test scaffolding in Apr 2026. Certora has an AI Composer (MEDIUM).",
      },
      {
        n: 7,
        name: "AI-native verified code, \"vericoding\"",
        verdict: "EMERGING",
        mode: "deeper",
        path: "B",
        evaluator: "The Lean kernel.",
        where: "United States only.",
        evidence:
          "Leanstral (Mistral, Apache-2.0, Mar 2026). Gauss formalised Viazovska's sphere packing proof in two weeks (MEDIUM).",
      },
      {
        n: 8,
        name: "Agentic chip design",
        verdict: "SERIOUS",
        verdictNote: "serious commercially, but mostly faster",
        mode: "faster",
        modeNote: "copilots are faster, the deeper part is pre-revenue",
        path: "A",
        evaluator:
          "Simulators, formal tools, coverage and tape-out. The best evaluator in this whole map.",
        where: "On site in California, and it wants an RTL or UVM background. Chipmind (Zurich, $2.5M pre-seed) is the only European entrant.",
        evidence:
          "ChipAgents raised $134M and self-reports \"6x ARR\" (MEDIUM). ChipStack was acquired by Cadence in Nov 2025 (MEDIUM). Cognichip raised $60M, Astrus has first paid pilots (MEDIUM).",
      },
      {
        n: 9,
        name: "RL environments and long-horizon engineering",
        verdict: "EMERGING",
        verdictNote: "hot, but it is a supplier business to about five labs",
        mode: "deeper",
        path: "B",
        pathNote: "B economics, A urgency",
        evaluator: "An emulator oracle or a verifier, one per environment.",
        where: "Prime Intellect in San Francisco. General Reasoning in London, around $11M.",
        evidence:
          "Mechanize raised $9.1M and is reported to be in talks worth over $1.5B for a Google hire-and-license deal (MEDIUM, unconfirmed). Prime Intellect raised a $130M Series A (MEDIUM). a16z says the labs build in-house; Wing VC predicts 20 suppliers consolidating to 3 to 5.",
      },
    ],
  },
  {
    id: "climate",
    title: "Climate, energy, earth systems",
    digest:
      "Materials work (CuspAI at $2.6B) is real, but its revenue gravity has drifted to semiconductors. Grid and trading software is the only sub-industry here with real customer-facing jobs, and it is optimisation with an AI label on it. Weather models now beat government forecasting but monetise through hedge funds. Fusion and minerals are closed shops. Carbon MRV is in retreat. Catastrophe-risk pricing is the underrated deeper-with-a-P&L niche.",
    rows: [
      {
        n: 10,
        name: "Materials for climate: sorbents, batteries, catalysts",
        verdict: "SERIOUS",
        verdictNote: "serious deep tech, emerging as \"climate\"",
        mode: "deeper",
        path: "B",
        evaluator: "Partner labs, or autonomous synthesis at A*STAR.",
        where: "CuspAI's application-science track sits in Singapore today.",
        evidence:
          "CuspAI raised $450M Series B at $2.6B in Jul 2026 (MEDIUM). The revenue gravity is shifting to semiconductors and data-centre hardware, not carbon capture: Orbital Industries' May 2026 raise contains no carbon-capture language at all (HIGH).",
      },
      {
        n: 11,
        name: "Grid and energy dispatch and trading agents",
        verdict: "SERIOUS",
        verdictNote: "serious, but faster in kind",
        mode: "faster",
        modeNote: "optimisers with an AI label",
        path: "A",
        evaluator: "The power market settles every single trade.",
        where:
          "The only climate sub-industry with real German jobs: envelio in Cologne (230+ staff), Entrix in Munich (43M euro, 3 GW), suena in Hamburg (8M euro), enspired in Vienna, 1KOMMA5 degrees.",
        evidence:
          "Invertix (Munich, 6 people, Feb 2026) is the one agent-native German energy startup, and it is pure back-office maintenance automation (HIGH).",
      },
      {
        n: 12,
        name: "Weather and climate foundation models",
        verdict: "EMERGING",
        mode: "deeper",
        modeNote: "new sensing data plus world simulation",
        path: "B",
        evaluator: "Forecasts are scored daily against what actually happened.",
        where: "Jua in Zurich has no open positions. WindBorne, Silurian and Brightband are United States only.",
        evidence:
          "WindBorne raised a $37M Series B in Aug 2026 (HIGH). The customers are governments and hedge funds. The big labs give competing models away for free.",
      },
      {
        n: 13,
        name: "Mineral exploration",
        verdict: "SERIOUS",
        verdictNote: "serious, and closed",
        mode: "deeper",
        path: "B",
        evaluator: "The drill core.",
        where: "Nothing in Europe. KoBold is its own customer.",
        evidence: "KoBold at $537M and a $2.96B valuation (MEDIUM, year unconfirmed). Terra AI raised a $20M Series A with BHP (MEDIUM).",
      },
      {
        n: 14,
        name: "Fusion and plasma control",
        verdict: "SERIOUS",
        verdictNote: "serious science, fantasy as a job market",
        mode: "deeper",
        modeNote: "deeper at DeepMind and CFS, not agentic at startups",
        path: "B",
        evaluator: "Plasma experiments, over multiple years.",
        where: "Proxima Fusion (Munich, 411M euro, around 200 staff). Its press release contains zero AI language (HIGH), and there are no customers to face.",
        evidence:
          "DeepMind and CFS built the TORAX simulator and ran RL over \"millions of virtual experiments\" (HIGH). That is a big-lab research deal, not a hiring market.",
      },
      {
        n: 15,
        name: "Agriculture and crop design",
        verdict: "EMERGING",
        mode: "mixed",
        modeNote: "deeper at Avalo, Phytoform and Inari; faster at Ecorobotix",
        path: "B",
        pathNote: "B for breeding, A for field robotics",
        evaluator: "Plant generations, which take years.",
        where: "United States, United Kingdom and Switzerland.",
        evidence: "Avalo raised $11M and breeds real plants to validate its model (MEDIUM). Ecorobotix raised $150M (MEDIUM).",
      },
      {
        n: 16,
        name: "Climate risk, catastrophe modelling, carbon MRV",
        verdict: "EMERGING",
        verdictNote: "split: risk pricing is heading to serious, carbon MRV is in retreat",
        mode: "deeper",
        modeNote: "simulating futures",
        path: "A",
        evaluator: "Underwriting profit and loss, and automatic parametric payouts.",
        where: "Mitiga in Barcelona, Overstory in Amsterdam. Reask and Floodbase sell to Swiss Re and Munich Re style buyers.",
        evidence:
          "Kettle simulates \"millions of wildfire scenarios\" and took RLI equity in Feb 2026 (MEDIUM). On the other side, Pachama laid off around 35% and was then acquired in Nov 2025, and voluntary carbon market trading value fell 75% (MEDIUM).",
      },
    ],
  },
  {
    id: "education",
    title: "Education and workforce retraining",
    digest:
      "Sixteen studies reviewed. Structured, short, human-mediated LLM tutoring works, worth roughly +0.26 to +0.31 standard deviations. Unsupervised always-on tutoring did not, per the Khanmigo two-year RCT. Nobody has shown multi-year individualised learning, and the benchmark evidence says agents cannot yet sustain quality for 30 days. Reskilling money is real but is measured on completion, not on skill or wages.",
    rows: [
      {
        n: 17,
        name: "K-12 personalised tutor agents",
        verdict: "EMERGING",
        verdictNote: "real but modest; the \"agent that knows you for years\" does not exist",
        mode: "mixed",
        modeNote: "deeper only at Eedi, which triggers on misconceptions; most of the rest is faster",
        path: "A",
        pathNote: "B for the memory and planning research",
        evaluator: "Randomised trials measuring learning gains.",
        where:
          "Eedi in London is the one product that notices misconceptions and runs proper RCTs. Knowunity in Berlin is consumer and on site. simpleclub is remote-first with an empty board.",
        evidence:
          "The Khanmigo two-year NBER RCT (Aug 2026) found 0.06 SD per year, and only 14.5% of messages contained any mathematical reasoning (MEDIUM). Structured, human-in-the-loop setups do work: Nigeria +0.31 SD, Sierra Leone +0.258 SD (HIGH), Tutor CoPilot +9 percentage points for weak tutors. EduClaw-Bench found that no agent sustains tutoring quality over 30 simulated days (HIGH).",
      },
      {
        n: 18,
        name: "AI-first schools: Alpha, 2 Hour Learning, Timeback",
        verdict: "FANTASY",
        verdictNote: "fantasy as evidence, emerging as a business",
        mode: "deeper",
        modeNote: "deeper by design, marketing by evidence",
        path: "A",
        evaluator: "Internal MAP data, and nothing else.",
        where: "United States only.",
        evidence:
          "No independent study exists. Pennsylvania rejected the charter over its performance claims, and there is heavy student surveillance (HIGH, NEPC Feb 2026).",
      },
      {
        n: 19,
        name: "Adult reskilling and workforce agents",
        verdict: "EMERGING",
        verdictNote: "emerging commercially, weak loop",
        mode: "faster",
        path: "A",
        evaluator: "Completion and satisfaction. Not skill, and not wages.",
        where:
          "Multiverse ($70M Series D at 1.65B pounds, May 2026) owns Berlin's StackFuel. Sana went to Workday for $1.1B in Nov 2025. Masterplan in Bochum, around 100 people.",
        evidence:
          "Multiverse is under scrutiny from the DfE, Ofsted and the press for below-average completions (HIGH). Uplimit's \"94% completion\" figure is vendor-reported. No agentic pilot exists at the German federal employment agency.",
      },
      {
        n: 20,
        name: "Simulation-based skills training",
        verdict: "EMERGING",
        verdictNote: "emerging, and not yet actually agentic",
        mode: "faster",
        modeNote: "content libraries",
        path: "A",
        evaluator: "None published.",
        where: "United States (Transfr, Interplay). Consumer roleplay apps like Speak and Praktika.",
        evidence: "No outcome evidence was found for any of them.",
      },
    ],
  },
  {
    id: "misinfo",
    title: "Misinformation, social engineering, cyber",
    digest:
      "Narrative intelligence got its own Gartner quadrant and lost its paying platform customers in the same year. Deepfake social-engineering defence is the serious and genuinely deeper one: an agent phones your staff and you find out, per person, whether it worked. Autonomous offensive security has the hardest evaluator in the field. Agentic SOC sits at Gartner's peak of inflated expectations.",
    rows: [
      {
        n: 21,
        name: "Narrative intelligence and misinformation defence",
        verdict: "EMERGING",
        verdictNote: "emerging commercially, faster technically",
        mode: "faster",
        modeNote: "monitoring and scoring, with no \"did our counter-narrative work?\" loop",
        path: "A",
        evaluator: "None.",
        where: "No venture-backed German startup found, only publicly funded research (kuKI, 3.18M euro with CORRECTIV and FZI; DeMasKI).",
        evidence:
          "Gartner published its first Narrative Intelligence quadrant in Jun 2026 and Blackbird raised $28M in Jan 2026 (MEDIUM). But the buyer that actually paid, the platforms, walked away: Logically collapsed in Jul 2025 (HIGH), and an IFCN survey in Apr 2026 found 76% of fact-checkers \"vulnerable or in crisis\" (HIGH).",
      },
      {
        n: 22,
        name: "Deepfake and AI social-engineering defence",
        verdict: "SERIOUS",
        mode: "deeper",
        modeNote: "agent-generated deepfake vishing and phishing aimed at the customer's own staff",
        path: "A",
        evaluator: "Did the attack land? Binary, per person.",
        where: "SoSafe (Cologne, around 500 staff, 8 European offices) is the German incumbent. Sensity in Amsterdam is tiny and profitable.",
        evidence:
          "Adaptive Security raised $146.5M from OpenAI, a16z and Nvidia. Doppel raised $124M at over $600M. Pindrop is over $100M ARR (MEDIUM).",
      },
      {
        n: 23,
        name: "Autonomous offensive security",
        verdict: "SERIOUS",
        mode: "deeper",
        path: "A",
        pathNote: "A today, B at the frontier",
        evaluator:
          "The exploit works or it does not, and the bounty is paid or it is not. The hardest evaluator in security.",
        where: "Filigran hires solution engineers remotely in Germany. XBOW is remote-first and hiring in Europe, but engineers only. Horizon3 has an Amsterdam office.",
        evidence:
          "XBOW went to number one on HackerOne and raised a $120M Series C at over $1B in Mar 2026. Horizon3 raised $250M at over $2B in Aug 2026. Anthropic's Glasswing found 1,587 validated true positives in open source (HIGH), though at roughly 26% validation precision, so human triage is still the bottleneck.",
      },
      {
        n: 24,
        name: "Agentic SOC and autonomous defence",
        verdict: "EMERGING",
        verdictNote: "emerging, with a real AI-washing risk",
        mode: "faster",
        path: "A",
        evaluator: "Soft, because the ground truth is noisy.",
        where: "Sekoia in France (26M euro). Dream ($3B, Vienna office) has zero open roles.",
        evidence:
          "Gartner in Jun 2026 put AI SOC agents at the Peak of Inflated Expectations, called them \"embryonic\", and put penetration at 1 to 5% (HIGH via a vendor post).",
      },
    ],
  },
  {
    id: "societies",
    title: "Synthetic societies, deliberation, negotiation, personal agents",
    digest:
      "Individual-level twins are real science. Population-level prediction is unvalidated, and the naive \"simulate 10,000 people\" method is measurably worse than just asking the model for a distribution. AI mediation is undeployed research. Organisational twins turn out to be per-employee knowledge routing. The personal self-model startups were all absorbed or died. Negotiation agents are the quiet success story.",
    rows: [
      {
        n: 25,
        name: "Synthetic populations and market prediction",
        verdict: "EMERGING",
        verdictNote: "a credible core surrounded by froth",
        mode: "deeper",
        modeNote: "deeper is claimed, and unproven at population level",
        path: "A",
        pathNote: "A on the surface, B underneath",
        evaluator:
          "Nothing prospective. Nobody anywhere has pre-registered a validation against a later election or product launch.",
        where: "Artificial Societies in London (12 people). Aaru in New York. Simile in Palo Alto.",
        evidence:
          "Park et al. found that interview-grounded twins reach 83 to 86% of the human test-retest ceiling (HIGH). Verasight found roughly 4-point topline error but up to 20-point subgroup error, with inverted answers on novel questions (HIGH). Gong, Sanders and Schneier all argue that \"simulate 10,000 individuals\" is the worse method (HIGH). Aaru is valued at $1B tiered on under $10M ARR (HIGH). Simile raised $200M at $2B (HIGH).",
      },
      {
        n: 26,
        name: "Deliberation and AI mediation",
        verdict: "FANTASY",
        verdictNote: "fantasy as an industry, serious as research",
        mode: "faster",
        modeNote: "summarisation",
        path: "B",
        evaluator: "None. No output has been shown to change a binding decision.",
        where: "Go Vocal in Brussels ($4.1M) sells civic SaaS. Liquid Democracy e.V. in Berlin is a non-profit.",
        evidence:
          "The Habermas Machine authors said themselves, in Aug 2025, that there is no deployment and no evidence of any decision being affected (HIGH).",
      },
      {
        n: 27,
        name: "Organisational digital twins",
        verdict: "FANTASY",
        verdictNote: "fantasy as \"simulate the company\", emerging as per-person twins",
        mode: "faster",
        path: "A",
        evaluator: "None.",
        where: "United States only.",
        evidence:
          "Twin1 ($20M) and Viven ($35M) are per-employee knowledge twins (MEDIUM). No startup validates a simulated organisational decision against the real one.",
      },
      {
        n: 28,
        name: "Negotiation agents",
        verdict: "SERIOUS",
        mode: "deeper",
        modeNote: "negotiations that would never have happened now happen",
        path: "A",
        evaluator: "The deal closes or it does not, and the savings are measured.",
        where: "Pactum in Tallinn, with most roles fully remote. German customers include Otto, Henkel and Linde.",
        evidence:
          "60+ Global 2000 customers, a largest AI-negotiated deal of $140.5M, and a $54M Series C in Jun 2025 (HIGH).",
      },
      {
        n: 29,
        name: "Personal memory and long-term self-model agents",
        verdict: "FANTASY",
        verdictNote: "fantasy as a standalone product",
        mode: "faster",
        path: "A",
        evaluator: "None. No product tests whether its model of you actually predicts you.",
        where:
          "What survived is business-to-business agent-memory infrastructure: Cognee in Berlin (7.5M euro seed) and Interloom in Germany (14.2M euro seed).",
        evidence: "Limitless went to Meta (Dec 2025), Bee went to Amazon (Jul 2025), Dot shut down (Oct 2025) (MEDIUM).",
      },
      {
        n: 30,
        name: "Agent-based economic and policy simulation",
        verdict: "EMERGING",
        verdictNote: "research inside institutions rather than an industry",
        mode: "deeper",
        path: "B",
        evaluator: "Market data.",
        where: "The ECB, the Bundesbank and the BIS in Frankfurt run LLM-agent simulations as research (MEDIUM).",
        evidence: "Oxford INET in 2025: LLM-agent agent-based models are \"only now beginning\".",
      },
    ],
  },
  {
    id: "physical",
    title: "The physical world",
    digest:
      "AI-native engineering design is the best deeper sub-industry in the whole map: a physics solver scores the design, then the customer's test rig does. Robotics fleet learning is the strongest physical feedback loop found anywhere here. Healthcare agents are faster; healthcare discovery engines are deeper. City twins, earth observation and the big industrial suites are faster analytics wearing agent language.",
    rows: [
      {
        n: 31,
        name: "Urban, logistics and infrastructure digital twins",
        verdict: "EMERGING",
        verdictNote: "faster analytics with a twin wrapped around it",
        mode: "faster",
        modeNote: "faster today, deeper in the research papers",
        path: "A",
        evaluator: "A simulator whose fidelity against the real city is unproven.",
        where: "SimPlan in Hanau, a classic simulation consultancy, opened a Frankfurt \"House of Digital Transformation\" in May 2026 (MEDIUM). Plus NVIDIA partner integrators.",
        evidence:
          "\"NVIDIA city agents\" is a simulation-testing blueprint, not autonomous city operations (MEDIUM). The freight-decarbonisation work is an arXiv paper, not a company (HIGH).",
      },
      {
        n: 32,
        name: "AI-native engineering design and simulation",
        verdict: "SERIOUS",
        verdictNote: "serious, and genuinely deeper",
        mode: "deeper",
        path: "A",
        pathNote: "A and B",
        evaluator: "A physics solver first, then the customer's test rigs.",
        where: "PhysicsX in London, New York and Singapore. Neural Concept in Lausanne (100+ staff) states a German operation (HIGH).",
        evidence:
          "PhysicsX raised a $300M Series C at around $2.4B in Jun 2026 with Siemens and NVIDIA (MEDIUM). Neural Concept raised a $100M Series C in Dec 2025 (MEDIUM).",
      },
      {
        n: 33,
        name: "Healthcare beyond diagnosis",
        verdict: "SERIOUS",
        verdictNote: "serious on discovery, faster for the agent products",
        mode: "mixed",
        path: "B",
        pathNote: "B for discovery, A for the agents",
        evaluator: "The wet lab for discovery. Expert review or benchmarks for the agents.",
        where: "Owkin in Paris. The Rhein-Main pharma cluster (Boehringer Ingelheim, Merck in Darmstadt, Sanofi in Hoechst) is a natural customer base, but nobody has posted a partner-facing role there.",
        evidence:
          "TrialMatchAI reports 92% and over 90% accuracy, confirmed (HIGH, arXiv 2505.08508), but the evaluator is oncologist review. TxAgent's 211 tools are confirmed (MEDIUM). Owkin's own headline is \"70% faster target identification\", which tells you which side of the line it is on.",
      },
      {
        n: 34,
        name: "Robotics foundation models and fleet learning",
        verdict: "SERIOUS",
        verdictNote: "serious capital, emerging proof",
        mode: "deeper",
        path: "A",
        pathNote: "A and B",
        evaluator: "Physical pick success, measured across a deployed fleet.",
        where:
          "Sereact in Stuttgart runs deployment engineering at customer sites. NEURA has 271 open roles. Telekinesis, a TU Darmstadt spin-out building an \"agentic operating system\" for robots with Deutsche Bahn and Infineon, was a Frankfurt Forward finalist in 2026 (HIGH).",
        evidence:
          "Sereact reports over 1 billion production picks and raised around $116M Series B in Apr 2026 (MEDIUM). NEURA says \"up to $1.4B\" raised, and the \"up to\" is doing work there (MEDIUM). Robotics as a whole raised a record $55.8B in 2026 (MEDIUM).",
      },
      {
        n: 35,
        name: "Space and earth-observation agents",
        verdict: "EMERGING",
        verdictNote: "faster analytics with a defence tailwind",
        mode: "faster",
        path: "A",
        evaluator: "Detections checked against ground truth.",
        where: "OroraTech in Munich (180+ staff, HIGH) and constellr (Munich HQ, Freiburg office), both in the Rheinmetall and ICEYE joint venture. ESA's ESOC in Darmstadt does autonomy work.",
        evidence: "No verified company has agents that actually task satellites or close a loop.",
      },
      {
        n: 36,
        name: "Industrial process and manufacturing agents",
        verdict: "SERIOUS",
        verdictNote: "serious market, split on depth",
        mode: "mixed",
        modeNote: "faster at Siemens and Celonis, deeper at Phaidra",
        path: "A",
        evaluator: "Process KPIs, or in Phaidra's case an actual physical setpoint response.",
        where: "Siemens and Celonis hire in Germany at scale. Phaidra is remote-first.",
        evidence:
          "The Siemens agent stack makes no closed-loop plant claim at all (HIGH). Phaidra's \"30% cut to Google's cooling bill\" is company-sourced (HIGH that it was claimed).",
      },
    ],
  },
  {
    id: "wildcards",
    title: "Wildcards, or domains that were not on the original list",
    digest:
      "Twenty domains checked. The career-viable ones are financial crime, catastrophe and flood intelligence, generative site design, and world models as robot simulators. The most genuinely \"not possible before\" science here has no career path at all: a whole Herculaneum scroll read for the first time, animal-language playback loops, an epigenetic design-screen-retrain cycle.",
    rows: [
      {
        n: 37,
        name: "Financial-crime network discovery and AML",
        verdict: "SERIOUS",
        verdictNote: "serious, but faster with an edge",
        mode: "faster",
        modeNote: "faster, with a genuine network-discovery edge",
        path: "A",
        evaluator: "Regulator-confirmed suspicious activity reports and recovered funds. Slow and noisy.",
        where: "Hawk in Munich (238 staff, remote-eligible technical roles). Salv in Tallinn, Quantexa in London.",
        evidence:
          "Hawk raised a 52M euro Series C in Apr 2025 (HIGH). Its CEO claims \"twice as many previously undetected novel cases\" (HIGH that it was claimed).",
      },
      {
        n: 38,
        name: "Catastrophe and flood intelligence, parametric cover",
        verdict: "SERIOUS",
        mode: "deeper",
        modeNote: "a product that could not have existed before the measurement did",
        path: "A",
        evaluator:
          "Money pays out automatically on measured flood extent. The hardest evaluator in the whole map.",
        where: "Floodbase and Reask (Sydney and London), selling into European reinsurance.",
        evidence:
          "Floodbase: \"Europe's largest portfolio parametric flood policy has already triggered in Italy\" (20 Jan 2026). Customers include Swiss Re, the WFP and African Risk Capacity (HIGH).",
      },
      {
        n: 39,
        name: "Generative site and floor-plan design",
        verdict: "SERIOUS",
        mode: "deeper",
        modeNote: "deeper-ish: parametric search rather than LLM agents",
        path: "A",
        evaluator: "Pro forma, zoning and parking count, all instant.",
        where: "Finch 3D in Malmo, Autodesk Forma in Oslo, TestFit in Dallas.",
        evidence: "TestFit raised a $20M Series A (HIGH). Forma shipped its \"Building Layout Explorer\" in Apr 2026 (MEDIUM).",
      },
      {
        n: 40,
        name: "World models as robot simulators",
        verdict: "EMERGING",
        verdictNote: "emerging, and heading towards serious",
        mode: "deeper",
        path: "B",
        pathNote: "B moving to A",
        evaluator: "Sim-to-real transfer.",
        where: "Decart in Israel, World Labs in San Francisco. Nothing in Europe.",
        evidence:
          "Decart raised $100M at $3.1B in Aug 2025 and claims profitability with NVIDIA as a customer (HIGH that it is self-reported). World Labs acquired SceniX in Jul 2026 (HIGH).",
      },
      {
        n: 41,
        name: "Humanitarian closed loops: malaria, anticipatory action",
        verdict: "EMERGING",
        mode: "deeper",
        path: "A",
        pathNote: "A and B",
        evaluator: "Mosquito counts and case counts.",
        where: "Zzapp in Israel, tiny, with donor-shaped customers.",
        evidence: "A 52.5% reduction in cases in Sao Tome at $0.44 per person, self-reported (HIGH that it was claimed).",
      },
      {
        n: 42,
        name: "Decoding animal communication",
        verdict: "EMERGING",
        verdictNote: "emerging science, fantasy as a career",
        mode: "deeper",
        path: "B",
        evaluator: "Playback experiments, which are a real closed loop.",
        where: "Earth Species Project is fully remote on a $13M budget, but its 2026 hires were a Director of Development and a Senior Accountant. Project CETI is in Dominica. No customers anywhere.",
        evidence: "ESP has defined a field it calls \"Animal Language Processing\" (HIGH). Funding is 100% philanthropic.",
      },
      {
        n: 43,
        name: "Archaeology and ancient texts",
        verdict: "SERIOUS",
        verdictNote: "serious science, fantasy as a career",
        mode: "deeper",
        path: "B",
        evaluator: "Papyrologists confirm whether the Greek is legible.",
        where: "No company exists. Archipanion in Germany does archive metadata, which is faster.",
        evidence:
          "The Vesuvius Challenge read an entire Herculaneum scroll end to end on 25 Jun 2026 (HIGH). DeepMind open-sourced Aeneas with no product attached (HIGH).",
      },
      {
        n: 44,
        name: "Longevity closed loop",
        verdict: "SERIOUS",
        verdictNote: "serious science, deep tech only",
        mode: "deeper",
        path: "B",
        evaluator: "Pooled screens feeding back into retraining.",
        where: "NewLimit in the San Francisco Bay Area, hiring wet-lab and ML people only.",
        evidence:
          "NewLimit reports \"thousands of designs tested simultaneously\" (HIGH). Retro Bio makes no AI claim at all (HIGH by absence).",
      },
      {
        n: 45,
        name: "Longitudinal mental-health agents",
        verdict: "FANTASY",
        verdictNote: "fantasy as deeper, and the industry is retreating",
        mode: "faster",
        modeNote: "faster, or substituting for a person",
        path: "A",
        evaluator: "PHQ-9 trajectories in principle. Nobody closes the loop in practice.",
        where: "Not recommended.",
        evidence:
          "Woebot shut its app down on 30 Jun 2025. Slingshot withdrew \"Ash\" from the United Kingdom on 23 Jan 2026 over device regulation (HIGH via STAT).",
      },
      {
        n: 46,
        name: "OSINT for journalism, astronomy agents, law-as-system simulation, sports biomechanics, forensic cold cases, low-resource languages, heritage and archive AI",
        verdict: "FANTASY",
        verdictNote: "fantasy or faster, across the board",
        mode: "faster",
        path: "A",
        evaluator: "None found in any of them.",
        where: "Lelapa AI pivoted to contact-centre transcription (HIGH). The Bundesarchiv's Kurrent handwriting recognition is public-sector digitisation.",
        evidence:
          "Astronomy agents score 15% task success on ScienceBoard (HIGH, arXiv). No funded newsroom-agent startup was found at all.",
      },
    ],
  },
];

export const conviction = [
  {
    title: "Test 1: who settles the score, and how fast?",
    points: [
      {
        lead: "Settled by money or physics within hours or days",
        arrow: "SERIOUS",
        body:
          "every single time. The power market, a signed contract, an exploit that runs, an employee who clicks the deepfake, a robot pick that succeeds, a parametric payout, a tape-out.",
      },
      {
        lead: "Settled by a model of the world, or by a panel of humans",
        arrow: "EMERGING",
        body:
          "at best. City twins validated against nothing, synthetic populations validated against their own consistency, trial matching validated by oncologist review, narrative-intelligence \"scores\".",
      },
      {
        lead: "Settled by nobody",
        arrow: "FANTASY",
        body:
          "or merely faster. AI mediation, organisational simulation, personal self-models, an \"AI scientist\" with no lab attached.",
      },
    ],
  },
  {
    title: "Test 2: who pays, and is the payer structurally motivated?",
    points: [
      {
        lead: "Every collapse found this year had a donor, a platform, or an ESG budget as its buyer",
        body:
          "fact-checking (Logically, and 76% of IFCN members in crisis), carbon MRV (Pachama, and a 75% fall in market value), mental-health chatbots (Woebot, Ash), animal communication and ancient texts (philanthropy only).",
      },
      {
        lead: "Everything serious has a profit-and-loss line as its buyer",
        body:
          "fraud losses, pentest budgets, procurement savings, warehouse throughput, reinsurance pricing, drug-programme milestones.",
      },
      {
        lead: "A corollary worth sitting with",
        body:
          "the \"climate\" money in 2026 is really compute and semiconductor money (CuspAI, Orbital) and power-market money (Entrix, envelio). The climate narrative is the marketing, not the customer.",
      },
    ],
  },
  {
    title: "Test 3: is the agent the thing, or the label?",
    points: [
      {
        lead: "Truly agentic and deeper",
        body:
          "self-driving labs, offensive-security agents, negotiation agents, evolutionary code search, social-engineering simulation, protein design campaigns.",
      },
      {
        lead: "Deeper but not agentic",
        body:
          "optimisation, physics or measurement with an AI label on the box: grid dispatch, catastrophe models, weather models, generative floor plans, fusion control at startups, mineral exploration. Interesting work, but not agents.",
      },
      {
        lead: "Agentic but faster",
        body:
          "agentic SOC, AI-scientist literature tools, chip copilots, Owkin's K Pro, Siemens Industrial Copilot, Celonis, Invertix, AML triage.",
      },
    ],
  },
];

export const namedIndustries = [
  {
    title: "Climate",
    body:
      "Serious money and real evaluators both exist. But the deeper work, meaning materials discovery, weather models, fusion control, mineral exploration and crop genomics, sits in laboratories in the United Kingdom, Switzerland and the United States, or in Munich hardware companies, and its revenue has quietly drifted towards semiconductors and data centres. The businesses that are actually hiring in Germany (envelio, Entrix, suena, 1KOMMA5 degrees, enspired) are hard-evaluator optimisation companies that are faster in kind. Carbon MRV is in retreat. The one honest \"deeper with a P&L\" climate-adjacent niche is catastrophe and flood intelligence, whose buyers are the big reinsurers.",
  },
  {
    title: "Personalised education",
    body:
      "Nobody has demonstrated individualised education in the full sense: multi-year memory of a learner, noticing misconceptions, coordinating with teachers and parents, and measuring mastery. What is demonstrated is narrower. Short, structured, human-in-the-loop LLM tutoring moves scores by 0.26 to 0.31 standard deviations in six to eight weeks. Unsupervised always-on tutors added roughly nothing over pre-AI practice software in the only two-year trial (Khanmigo, 0.06 SD per year). Gaps widened in two of the three positive RCTs. No current agent sustains tutoring quality for 30 simulated days. The closest existing product is Eedi in London, which triggers on misconceptions, stays supervised, and runs proper trials with DeepMind. The revolution is visible in research pipelines, not in outcome data.",
  },
  {
    title: "Workforce retraining",
    body:
      "The money is real: Multiverse at $70M on a 1.65B pound valuation, Workday paying $1.1B for Sana, and Germany subsidising AI reskilling up to 100% through its QCG and AZAV schemes. But the published metrics are completion and satisfaction, not skill or wage gains, and the one externally audited player is under regulator and press scrutiny for below-average completions. The genuinely deeper version, an agent that builds a personal reskilling path, coaches over months, and is judged on whether you got a job, was not found anywhere.",
  },
  {
    title: "Misinformation and social engineering, on the defence side",
    body:
      "A split verdict. Narrative and misinformation defence is sliding from \"fact-checking for platforms\", which collapsed, to \"narrative risk for executives and governments\", which is monitoring rather than a closed loop, and has no German startup at all; the German route is publicly funded research. AI social-engineering defence is serious and genuinely deeper: agent-generated deepfake vishing and phishing against a customer's own staff, with a binary outcome per person. Autonomous offensive security has the hardest evaluator in security.",
  },
];

export const corrections = [
  { claim: "ChipStack is an independent startup", fix: "Acquired by Cadence in Nov 2025, now a Cadence product line", conf: "MEDIUM" },
  { claim: "Mechanize \"built a GBA emulator in 24h\"", fix: "It is GBA Eval, a benchmark Mechanize runs on other labs' models (best score 70.9%). Not a Mechanize engineering feat", conf: "MEDIUM" },
  { claim: "Mechanize \"$1.5B\"", fix: "Reported talks about a Google hire-and-license deal, not a round and not a closed acquisition", conf: "MEDIUM" },
  { claim: "Tensormesh does kernel generation", fix: "Inference caching and routing. Mis-categorised", conf: "MEDIUM" },
  { claim: "Axiom Math \"$100M at $1.5B\"", fix: "$200M Series A at $1.6B, Mar 2026", conf: "HIGH" },
  { claim: "\"DeepEvolve\", \"Standard Kernel\"", fix: "Could not verify that either exists", conf: "UNVERIFIED" },
  { claim: "Orbital Materials does carbon capture", fix: "Now Orbital Industries. The May 2026 raise is data-centre cooling hardware, and carbon capture is absent from it", conf: "HIGH" },
  { claim: "Atomic Canyon does nuclear control", fix: "Document search and RAG for licensing and operations. Faster, not control", conf: "MEDIUM" },
  { claim: "Pachama does carbon MRV", fix: "Laid off around 35% in Jun 2025, acquired by Carbon Direct in Nov 2025, no longer independent", conf: "MEDIUM" },
  { claim: "Proxima Fusion is a Munich AI employer", fix: "Its 411M euro press release contains no AI language. It is physics and hardware deep tech", conf: "HIGH" },
  { claim: "constellr is based in Freiburg", fix: "Munich HQ. Freiburg is an office", conf: "HIGH" },
  { claim: "NVIDIA \"city agents\"", fix: "An Omniverse blueprint for smart-city AI: sim-ready twins for testing vision agents, not autonomous city operations", conf: "MEDIUM" },
  { claim: "\"Agentic urban logistics for freight decarbonisation\"", fix: "An arXiv paper (2506.13068), not a company", conf: "HIGH" },
  { claim: "Unlearn has \"FDA-approved synthetic control arms\"", fix: "Overstated in secondary sources. It is an EMA qualification opinion plus covariate adjustment", conf: "LOW" },
  { claim: "NEURA raised \"$1.4B\"", fix: "NEURA's own headline says \"up to $1.4B\"", conf: "MEDIUM" },
  { claim: "\"CyberJustice Tutor 2026, 123 participants\"", fix: "The study exists (arXiv 2603.18470) but measured usability only, with no learning outcomes. Do not cite it as evidence that tutoring works", conf: "HIGH" },
  { claim: "Alpha School delivers \"2x learning\"", fix: "Internal MAP analyses only, no independent study, and Pennsylvania rejected the charter", conf: "HIGH" },
  { claim: "Sana is a Stockholm startup", fix: "Acquired by Workday for $1.1B, closed 4 Nov 2025", conf: "HIGH" },
  { claim: "Fobizz was \"acquired\"", fix: "No acquisition found. The site names 101skills GmbH", conf: "UNVERIFIED" },
  { claim: "Hadrian raised a \"$1.4B Series D, Aug 2026\"", fix: "Aggregator-only, undisclosed backers, absent from hadrian.io. Do not repeat it. Likely confusion with the United States defence manufacturer of the same name", conf: "HIGH" },
  { claim: "\"OpenAI report: 40% of phishing by AI agents\"", fix: "Third-party blogs, not OpenAI. Treat as likely fabricated", conf: "LOW" },
  { claim: "Lakera is a Zurich startup", fix: "Now part of Check Point, around $300M, Sep 2025", conf: "MEDIUM" },
  { claim: "GetReal Labs", fix: "Renamed GetReal Security. $17.5M Series A in Mar 2025", conf: "MEDIUM" },
  { claim: "Logically", fix: "Filed for administration in Jul 2025, pre-pack sale to Kreatur Ltd", conf: "HIGH" },
  { claim: "Altana does organisational simulation", fix: "A supply-chain knowledge graph. Removed from that cluster", conf: "MEDIUM" },
  { claim: "Polis and vTaiwan are \"AI-assisted\"", fix: "Polis is statistics over votes, with no LLM agents. The LLM layer is Jigsaw Sensemaker and Talk to the City", conf: "MEDIUM" },
  { claim: "The Habermas Machine is a \"civic instrument\"", fix: "By its own authors' Aug 2025 account: no deployment, and no evidence of changing a decision", conf: "HIGH" },
  { claim: "Anagenex", fix: "Inactive", conf: "MEDIUM" },
  { claim: "Strateos", fix: "No longer independent, and the details conflict", conf: "UNVERIFIED" },
  { claim: "Limitless, Bee and Dot as personal agents", fix: "Meta (Dec 2025), Amazon (Jul 2025), and shut down (Oct 2025)", conf: "MEDIUM" },
];

export const heldUp =
  "FutureHouse spinning out Edison (Nov 2025, $70M seed). Lila at $550M by Oct 2025, now reportedly raising at $8.5B pre-money. Periodic Labs. Harmonic at $120M and $1.45B with Aristotle. The Tao and AlphaEvolve 67-problem paper. The Qiushi Discovery Engine paper, real and academic. The Sakana critiques. Dream at $260M and $3B (Jun 2026). XBOW at number one on HackerOne. Aaru's Accenture investment and EY customer. Simile as a Stanford spinout. Artificial Societies in YC W25. TrialMatchAI at 92% and over 90%. TxAgent's 211 tools. Variant Bio's \"Inference\" agents. Isomorphic's $2.1B on 12 May 2026. The World Bank's Nigeria result at +0.31 SD, which came from teacher-facilitated pairs rather than an autonomous agent. Pactum's Walmart and Maersk deals.";

export const methodNotes = [
  "Eight research agents ran in parallel, one per cluster, each starting with a keyword-generation step, then roughly 25 to 75 web calls each.",
  "The session-wide search quota (200 searches) ran out partway through in five of the eight agents. They fell back to fetching company sites, Ashby posting-API JSON, Greenhouse boards, arXiv and raw result pages directly.",
  "Anything that could not be reached is marked UNVERIFIED rather than guessed. There are roughly 40 such names across the full reports.",
  "The Ashby posting API was by far the most reliable signal for what a company is actually hiring for and where. Several careers pages are JavaScript-rendered and simply cannot be fetched.",
  "Aggregator-only funding and location claims were caught being wrong again, in three separate cases. Only a company's own pages count.",
];

export const keywordBank = [
  {
    cluster: "Autonomous science",
    keywords:
      "self-driving lab, closed-loop materials discovery, autonomous experimentation platform, lab-in-the-loop protein design, robotic cloud lab, active-learning synthesis, agentic protocol generation, hypothesis engine, materials acceleration platform, science data factory, digital chemistry, chemputation, autonomes Labor KI Startup, Materialforschung KI Startup, Laborautomatisierung KI",
  },
  {
    cluster: "Math, formal methods, chips, RL",
    keywords:
      "evolutionary coding agent, autoformalization startup, neural theorem proving company, Lean 4 verified AI, mathematical superintelligence, vericoding, reasoning-as-a-service, agentic EDA, LLM RTL generation, analog circuit design agent, physics-informed chip foundation model, RL environments hub, long-horizon benchmark, GPU kernel generation agent, reward hacking environments",
  },
  {
    cluster: "Climate and energy",
    keywords:
      "AI-discovered carbon-capture sorbent, generative MOF design, autonomous grid dispatch agent, virtual power plant AI agent, battery multi-market optimisation, data-centre demand flexibility, flexible grid interconnection, plasma control RL, stellarator optimisation AI, AI weather balloons, earth-system foundation model, parametric wildfire reinsurance, crop breeding foundation model, carbon-removal MRV agent, AI for nuclear licensing, Klimatech KI Agent Startup Deutschland, Energiehandel KI Startup, Netzbetreiber KI Agent",
  },
  {
    cluster: "Education and retraining",
    keywords:
      "AI tutor RCT learning gains, mastery learning agent, longitudinal student model, Bloom two-sigma AI evidence, AI-first school critique, Tutor CoPilot, misconception-triggered tutor, AI apprenticeship coach, reskilling agent unemployed, skills-graph career pathing, simulation-based nurse training AI, KI Nachhilfe Startup, Weiterbildung KI Agent, AZAV Bildungsgutschein KI",
  },
  {
    cluster: "Misinformation and cyber",
    keywords:
      "narrative intelligence platform, coordinated inauthentic behaviour detection, cognitive security, FIMI detection EEAS, deepfake voice fraud defence, AI vishing simulation, human risk management platform, autonomous penetration testing agent, adversarial exposure validation, CTEM, agentic SOC, autonomous vulnerability discovery, AI bug-bounty agent, synthetic media provenance C2PA, DSA compliance AI, Desinformation KI Startup Deutschland, KI Pentest Startup",
  },
  {
    cluster: "Synthetic societies",
    keywords:
      "synthetic respondents, silicon sampling accuracy, digital twin consumer panel, generative agent simulations of 1,000 people, AI mediator Habermas machine, sensemaking platform Polis, AI-assisted citizens' assembly, agent-based economic simulation central bank LLM, LLM depositors bank run, autonomous negotiation agent procurement, war-gaming LLM agents, lifelong memory agent, personal AI wearable memory, Meinungsforschung KI synthetische Befragte, Buergerrat KI",
  },
  {
    cluster: "Physical world",
    keywords:
      "AI-native engineering simulation, physics surrogate generative design, agentic CAE, urban digital twin agent, agentic freight decarbonisation, in-silico clinical trial digital twin, therapeutic reasoning agent, robot foundation model, fleet learning physical outcomes, sim-to-real autonomous data collection, satellite tasking agent, earth-observation foundation model, closed-loop process optimisation chemical plant, fab yield agent, agentic PLC programming, Industrie 4.0 KI Agent Startup, Robotik Startup Stuttgart Foundation Model",
  },
  {
    cluster: "Wildcards",
    keywords:
      "animal communication playback experiment, bioacoustics foundation model, Herculaneum scroll virtual unwrapping, lost-language decipherment, OSINT agents investigative journalism, generative site planning feasibility, AML graph investigation agents, cross-bank intelligence exchange, anticipatory action parametric trigger, malaria elimination AI, poverty targeting satellite, AI therapy chatbot regulation, epigenetic reprogramming discovery engine, autonomous telescope agent, injury-risk forecasting agent, agent-based legislation simulation, eDNA biodiversity monitoring, synthetic event set catastrophe model, forensic genetic genealogy, interactive world model robotics, Katastrophenmodell Versicherung KI, Geldwaeschepraevention KI Agent",
  },
];

export const taxonomy = [
  { letter: "A", title: "World simulation", body: "What would happen if?" },
  { letter: "B", title: "Mind simulation", body: "How will different minds interpret this?" },
  { letter: "C", title: "Search over possible designs", body: "What solution would humans never think to try?" },
  { letter: "D", title: "Collective intelligence", body: "What does a group actually believe, and where can it agree?" },
  { letter: "E", title: "Continuous adaptation", body: "How should the system change as reality changes?" },
];
